import React from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Avatar,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Lock as LockIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
} from "@mui/icons-material";
import { lightTheme } from "../../styles/theme";
import {
  MANAGEMENT_ACTION_ICON_BUTTON_SIZE,
  MANAGEMENT_ACTION_ICON_FONT_SIZE,
  MANAGEMENT_ACTION_ROW_SX,
} from "../../styles/managementCardActions";
import {
  MANAGEMENT_CARD_META_FONT_SIZE,
  MANAGEMENT_CARD_TITLE_FONT_SIZE,
} from "../../styles/managementCardTypography";
import {
  MANAGEMENT_AVATAR_FONT_SIZE,
  MANAGEMENT_AVATAR_SIZE,
} from "../../styles/managementShared";

export interface CircleRowData {
  circleId: string;
  name: string;
  description: string;
  isPublic: boolean;
  createdAt: string;
  isSubCircle: boolean;
  logo: string;
}

interface CircleTableRowProps {
  circle: CircleRowData;
  isMobile: boolean;
  dragHandle?: React.ReactNode;
  isDragging?: boolean;
  onOpen?: (circleId: string) => void;
}

const CircleTableRow: React.FC<CircleTableRowProps> = ({
  circle,
  isMobile,
  dragHandle,
  isDragging = false,
  onOpen,
}) => {
  const styledTheme = lightTheme;
  const hasDragHandle = Boolean(dragHandle);
  const desktopColumns = hasDragHandle
    ? "auto 1.8fr 1.8fr 1.1fr 1.1fr 1fr"
    : "1.8fr 1.8fr 1.1fr 1.1fr 1fr";

  return (
    <Paper
      elevation={0}
      onClick={() => {
        if (circle.isSubCircle) {
          onOpen?.(circle.circleId);
        }
      }}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: { xs: 2, sm: 2.5 },
        minHeight: { xs: "auto", sm: 80 },
        position: "relative",
        cursor: circle.isSubCircle ? "pointer" : "default",
        opacity: isDragging ? 0.85 : 1,
        boxShadow: isDragging ? 4 : "none",
        "&:hover": {
          backgroundColor: "action.hover",
        },
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : desktopColumns,
          gap: isMobile ? 1 : 2,
          alignItems: "center",
        }}
      >
        {!isMobile && dragHandle && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {dragHandle}
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1.5, sm: 2 },
            pr: isMobile ? 10 : 0,
          }}
        >
          {isMobile && dragHandle && (
            <Box sx={{ display: "flex", alignItems: "center" }}>{dragHandle}</Box>
          )}
          <Avatar
            src={
              circle.logo &&
              circle.logo.trim() !== "" &&
              !circle.logo.includes("placehold.co")
                ? circle.logo
                : "/placeholder.jpg"
            }
            sx={{
              width: MANAGEMENT_AVATAR_SIZE,
              height: MANAGEMENT_AVATAR_SIZE,
              fontSize: MANAGEMENT_AVATAR_FONT_SIZE,
            }}
          />
          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 400, fontSize: MANAGEMENT_CARD_TITLE_FONT_SIZE }}
            >
              {circle.name}
            </Typography>
            {circle.isSubCircle && (
              <Typography variant="caption" color="text.secondary">
                Sub-circle
              </Typography>
            )}
          </Box>
        </Box>
        {isMobile && (
          <Typography
            variant="body2"
            sx={{
              position: "absolute",
              top: 14,
              right: 16,
              color: "text.secondary",
              fontSize: MANAGEMENT_CARD_META_FONT_SIZE,
              lineHeight: 1.2,
            }}
          >
            {new Date(circle.createdAt).toLocaleDateString()}
          </Typography>
        )}
        {!isMobile && (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              minHeight: "40px",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: MANAGEMENT_CARD_META_FONT_SIZE,
                lineHeight: 1.4,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {circle.description || "No description"}
            </Typography>
          </Box>
        )}
        {isMobile && (
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: MANAGEMENT_CARD_META_FONT_SIZE,
                lineHeight: 1.35,
                mb: 0.5,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {circle.description || "No description"}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1.5,
                mt: 0.5,
                width: "100%",
              }}
            >
              <Chip
                icon={circle.isPublic ? <VisibilityIcon /> : <LockIcon />}
                label={circle.isPublic ? "Public" : "Private"}
                size="small"
                color={circle.isPublic ? "primary" : "default"}
                sx={{
                  ...(circle.isPublic && {
                    "& .MuiChip-label": {
                      color: "common.white",
                    },
                    "& .MuiChip-icon": {
                      color: "common.white",
                    },
                  }),
                }}
              />
            </Box>
          </Box>
        )}
        {!isMobile && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Chip
              icon={circle.isPublic ? <VisibilityIcon /> : <LockIcon />}
              label={circle.isPublic ? "Public" : "Private"}
              size="small"
              color={circle.isPublic ? "primary" : "default"}
              sx={{
                ...(circle.isPublic && {
                  "& .MuiChip-label": {
                    color: "common.white",
                  },
                  "& .MuiChip-icon": {
                    color: "common.white",
                  },
                }),
              }}
            />
          </Box>
        )}
        {!isMobile && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontSize: MANAGEMENT_CARD_META_FONT_SIZE }}
            >
              {new Date(circle.createdAt).toLocaleDateString()}
            </Typography>
          </Box>
        )}
        {!isMobile && (
          <Box
            className="action-buttons"
            sx={{
              ...MANAGEMENT_ACTION_ROW_SX,
            }}
          >
            {circle.isSubCircle && (
              <IconButton
                size={MANAGEMENT_ACTION_ICON_BUTTON_SIZE}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen?.(circle.circleId);
                }}
                sx={{ color: styledTheme.colors.primary }}
                title="Open circle"
              >
                <ArrowForwardIosIcon fontSize={MANAGEMENT_ACTION_ICON_FONT_SIZE} />
              </IconButton>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default React.memo(CircleTableRow);
